import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  Download, 
  Building2, 
  Users, 
  UserCheck, 
  Clock, 
  TrendingUp,
  Calendar,
  BarChart3,
  PieChart
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { useLanguage } from '@/i18n/LanguageContext';

function ReportsPage() {
  const { t, isRTL } = useLanguage();
  const [reportType, setReportType] = useState('monthly-attendance');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reportData, setReportData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const reportTypes = [
    { value: 'monthly-attendance', label: 'Monthly Attendance', icon: Calendar },
    { value: 'monthly-break', label: 'Monthly Break', icon: Clock },
    { value: 'late-trends', label: 'Late Trends', icon: TrendingUp },
    { value: 'break-analysis', label: 'Break Analysis', icon: BarChart3 },
  ];
  
  useEffect(() => {
    fetchSummary();
    fetchCompanies();
  }, []);
  
  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/reports/summary`, {
        withCredentials: true
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };
  
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/companies`, {
        withCredentials: true
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };
  
  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (selectedCompany !== 'all') {
        params.company_id = selectedCompany;
      }
      
      const response = await axios.get(
        `${API}/superadmin/reports/${reportType}`,
        { params, withCredentials: true }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportReport = () => {
    if (!reportData) return;
    
    const csv = convertToCSV(reportData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}-${month}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const convertToCSV = (data) => {
    if (!data.records || data.records.length === 0) return '';
    
    const headers = Object.keys(data.records[0]).join(',');
    const rows = data.records.map(record =>
      Object.values(record).map(val => `"${val}"`).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };
  
  // Aggregate data for charts
  const getChartData = () => {
    if (!reportData?.records) return [];
    
    const dateMap = {};
    reportData.records.forEach(record => {
      const date = record.date;
      if (!dateMap[date]) {
        dateMap[date] = { date, present: 0, late: 0, absent: 0 };
      }
      if (record.status === 'present') dateMap[date].present++;
      else if (record.status === 'late') dateMap[date].late++;
      else if (record.status === 'absent') dateMap[date].absent++;
    });
    
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  };
  
  const summaryCards = [
    {
      title: 'Total Companies',
      value: summary?.summary?.total_companies || 0,
      subtitle: `${summary?.summary?.active_companies || 0} active`,
      icon: Building2,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100'
    },
    {
      title: 'Total Staff',
      value: summary?.summary?.total_staff || 0,
      subtitle: `${summary?.summary?.active_staff || 0} active`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Present Today',
      value: summary?.summary?.present_today || 0,
      subtitle: 'Checked in',
      icon: UserCheck,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Late Today',
      value: summary?.summary?.late_today || 0,
      subtitle: 'Arrived late',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    },
  ];
  
  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-gray-600 mt-1">Generate and export detailed reports</p>
        </div>
        <div className="text-right">
          <p className="text-4xl font-bold text-indigo-600">
            {summary?.monthly_stats?.avg_attendance_rate || 0}%
          </p>
          <p className="text-sm text-gray-500">Monthly Attendance Rate</p>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.title} className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${card.bgColor}`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-500 font-medium">{card.title}</p>
                  <p className="text-xs text-gray-400">{card.subtitle}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Tabs for different views */}
      <Tabs defaultValue="generate" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generate">Generate Report</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="company">Company Breakdown</TabsTrigger>
        </TabsList>
        
        {/* Generate Report Tab */}
        <TabsContent value="generate" className="space-y-6">
          <Card data-testid="report-generator">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Generate Report
              </CardTitle>
              <CardDescription>Select report type and parameters</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger id="report-type" data-testid="report-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {reportTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger id="company" data-testid="company-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {companies.map((company) => (
                        <SelectItem key={company.company_id} value={company.company_id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="month">Month</Label>
                  <Input
                    id="month"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    data-testid="month-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button
                    onClick={handleGenerateReport}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={loading}
                    data-testid="generate-report-button"
                  >
                    {loading ? 'Generating...' : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Report Results */}
          {reportData && (
            <>
              {/* Chart */}
              <Card data-testid="report-chart">
                <CardHeader>
                  <CardTitle>Attendance Trend</CardTitle>
                  <CardDescription>Daily attendance breakdown for {reportData.month}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="present" name="Present" fill="#10b981" />
                        <Bar dataKey="late" name="Late" fill="#f59e0b" />
                        <Bar dataKey="absent" name="Absent" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Table */}
              <Card data-testid="report-results">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Report Results</CardTitle>
                      <CardDescription>
                        {reportData.total_records} records found for {reportData.month}
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleExportReport}
                      data-testid="export-report-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {reportData.records.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No records found for the selected period
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-96">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("common.date")}</TableHead>
                            <TableHead>Staff ID</TableHead>
                            <TableHead>Company ID</TableHead>
                            <TableHead>{t("common.status")}</TableHead>
                            <TableHead>{t("attendance.check_in")}</TableHead>
                            <TableHead>{t("attendance.check_out")}</TableHead>
                            <TableHead>Late By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportData.records.slice(0, 100).map((record, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{record.date}</TableCell>
                              <TableCell className="text-xs">{record.staff_id}</TableCell>
                              <TableCell className="text-xs">{record.company_id}</TableCell>
                              <TableCell>
                                <span className={`
                                  px-2 py-1 rounded-full text-xs font-semibold
                                  ${record.status === 'present' ? 'bg-green-100 text-green-700' : ''}
                                  ${record.status === 'absent' ? 'bg-red-100 text-red-700' : ''}
                                  ${record.status === 'late' ? 'bg-amber-100 text-amber-700' : ''}
                                `}>
                                  {record.status || 'N/A'}
                                </span>
                              </TableCell>
                              <TableCell>
                                {record.check_in ? new Date(record.check_in).toLocaleTimeString() : '-'}
                              </TableCell>
                              <TableCell>
                                {record.check_out ? new Date(record.check_out).toLocaleTimeString() : '-'}
                              </TableCell>
                              <TableCell>
                                {record.late_by ? `${record.late_by} min` : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {reportData.records.length > 100 && (
                        <div className="text-center text-sm text-gray-500 mt-4 py-2 bg-gray-50 rounded">
                          Showing first 100 of {reportData.records.length} records. Export to see all.
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        
        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  Monthly Overview
                </CardTitle>
                <CardDescription>{summary?.monthly_stats?.month || 'Current Month'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-indigo-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-indigo-600">
                      {summary?.monthly_stats?.total_work_days || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Work Days</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {summary?.monthly_stats?.total_present || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Total Present</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">
                      {summary?.monthly_stats?.total_late || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Total Late</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-red-600">
                      {summary?.monthly_stats?.total_absent || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Total Absent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-green-600" />
                  Attendance Rate
                </CardTitle>
                <CardDescription>Overall performance this month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="relative w-40 h-40">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#e5e7eb"
                        strokeWidth="12"
                        fill="none"
                      />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#4f46e5"
                        strokeWidth="12"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(summary?.monthly_stats?.avg_attendance_rate || 0) * 4.4} 440`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-bold text-indigo-600">
                        {summary?.monthly_stats?.avg_attendance_rate || 0}%
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Average Attendance Rate</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Company Breakdown Tab */}
        <TabsContent value="company" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600" />
                Company Performance
              </CardTitle>
              <CardDescription>Today's attendance by company</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Company Name</TableHead>
                    <TableHead>{t("dashboard.total_staff")}</TableHead>
                    <TableHead>{t("dashboard.present_today")}</TableHead>
                    <TableHead>Attendance Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(summary?.company_breakdown || []).map((company, index) => (
                    <TableRow key={company.company_id}>
                      <TableCell>
                        <span className={`
                          w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                          ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                            index === 1 ? 'bg-gray-100 text-gray-700' : 
                            index === 2 ? 'bg-orange-100 text-orange-700' : 
                            'bg-indigo-50 text-indigo-600'}
                        `}>
                          {index + 1}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">{company.company_name}</TableCell>
                      <TableCell>{company.total_staff}</TableCell>
                      <TableCell className="text-green-600 font-semibold">{company.present_today}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                company.attendance_rate >= 80 ? 'bg-green-500' : 
                                company.attendance_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${company.attendance_rate}%` }}
                            />
                          </div>
                          <span className={`font-semibold ${
                            company.attendance_rate >= 80 ? 'text-green-600' : 
                            company.attendance_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {company.attendance_rate}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!summary?.company_breakdown || summary.company_breakdown.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No company data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ReportsPage;
